/**
 * @format
 */
/* eslint-disable react/jsx-props-no-spreading */

import { Tooltip } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { api } from '../../api.ts';
import CacheManager from '../../utils/cache';
import RequiredFieldAdornment from '../app/field/RequiredFieldAdornment';

const filter = createFilterOptions();

// TODO: Prevent useless API calls (mount/unmount)

function CustomAutocomplete({
  endpoint,
  getOptionLabel,
  getOptionKey,
  isOptionEqualToValue,
  name,
  label,
  onChange,
  onCreated,
  onFocus,
  onBlur,
  value,
  size,
  required,
  disabled,
  autoLoad,
  allowNull,
  error,
  helperText,
  requiredFor,
  multiple,
  cache,
  creatable,
  tooltip,
  inputStyle,
  renderOption,
  fullWidth,
  ref,
  additionalParams,
  autoFocus,
  optionsFilter,
  options: optionsProp,
  popperZIndex,
}) {
  const isStatic = Array.isArray(optionsProp);
  const [options, setOptions] = useState(() => (isStatic ? optionsProp : []));
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  let timeout = null;

  let cacheInstance = null;
  if (cache && endpoint) {
    cacheInstance = CacheManager.instance.getModel(endpoint(''));
  }

  const applyOptionsFilter = (loadedOptions) =>
    optionsFilter ? optionsFilter(loadedOptions) : loadedOptions;

  const loadOptions = () => {
    if (isStatic) {
      setOptions(applyOptionsFilter(optionsProp));
      setLoading(false);
      return null;
    }
    if (cache) {
      setOptions(applyOptionsFilter(cacheInstance.list()));
      setLoading(false);
    }
    setLoading(true);
    return api
      .get(endpoint(input))
      .then(
        (result) =>
          new Promise((resolve) => {
            setOptions(
              applyOptionsFilter(
                Array.isArray(result.data)
                  ? result.data
                  : result.data.results ?? [],
              ),
            );
            setLoading(false);
            resolve();
          }),
      )
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const createOption = (option) => {
    setLoading(true);
    return api
      .post(endpoint(), {}, option)
      .then((result) => result)
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const handleInputChange = (event) => {
    if (!event) return;
    const { value: localValue } = event.target;
    setInput(localValue);
    if (isStatic) return;
    setLoading(true);
    setOpen(true);
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      if (localValue !== input) {
        loadOptions();
      }
    }, 500);
  };

  const handleChange = (event, newValue) => {
    if (creatable) {
      if (multiple && Array.isArray(newValue)) {
        const createCandidate = newValue.find((v) => v && v.inputValue);
        if (createCandidate?.inputValue) {
          const remaining = newValue.filter((v) => v && !v.inputValue);
          return createOption({
            name: createCandidate.inputValue,
            title: createCandidate.inputValue,
            label: createCandidate.inputValue,
            ...additionalParams,
          })
            .then((result) => {
              const created = result?.data;
              const nextValues = [...remaining, created].filter(Boolean);
              return onChange ? onChange(event, nextValues) : null;
            })
            .then(() => (onCreated ? onCreated() : null));
        }
      }
      if (typeof newValue === 'string') {
        event.preventDefault();
        return null;
      }
      if (newValue && newValue.inputValue) {
        return createOption({
          name: newValue.inputValue,
          title: newValue.inputValue,
          label: newValue.inputValue,
          ...additionalParams,
        })
          .then((result) => (onChange ? onChange(event, result?.data) : null))
          .then(() => (onCreated ? onCreated() : null));
      }
    }
    return onChange ? onChange(event, newValue) : null;
  };

  useEffect(() => {
    if (isStatic) {
      setOptions(applyOptionsFilter(optionsProp));
      setLoading(false);
    }
  }, [isStatic, optionsProp]);

  useEffect(() => {
    if (autoLoad && !isStatic) {
      loadOptions();
    }
  }, []);

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        setOpen(true);
        if (isStatic) {
          setLoading(false);
          return;
        }
        setLoading(!autoLoad);
        if (!autoLoad) {
          loadOptions();
        }
      }}
      onClose={() => {
        setOpen(false);
        setLoading(false);
      }}
      isOptionEqualToValue={
        isOptionEqualToValue || ((o, v) => o.id === (cache ? v : v?.id))
      }
      getOptionLabel={(o) => getOptionLabel(o) || ''}
      getOptionKey={getOptionKey || undefined}
      options={[...new Set(options)]}
      loading={loading}
      value={
        // eslint-disable-next-line no-nested-ternary
        multiple
          ? value.map((v) =>
              cacheInstance ? cacheInstance.get(v) : v || '',
            ) || []
          : cacheInstance
          ? cacheInstance.get(value) || ''
          : value || ''
      }
      onChange={handleChange}
      disabled={disabled}
      onInputChange={handleInputChange}
      noOptionsText="Aucun résultat trouvé"
      loadingText="Chargement..."
      size={size}
      filterOptions={
        creatable
          ? (opts, params) => {
              const filtered = filter(opts, params);
              if (creatable) {
                if (params.inputValue !== '') {
                  filtered.push({
                    inputValue: params.inputValue,
                    title: `Ajouter « ${params.inputValue} »`,
                    name: `Ajouter « ${params.inputValue} »`,
                    label: `Ajouter « ${params.inputValue} »`,
                  });
                }
              }
              return filtered;
            }
          : undefined
      }
      disableClearable={!allowNull}
      multiple={multiple}
      freeSolo={creatable}
      blurOnSelect
      componentsProps={
        popperZIndex ? { popper: { sx: { zIndex: popperZIndex } } } : undefined
      }
      renderOption={renderOption}
      renderInput={(params) => (
        <Tooltip
          title={tooltip.content}
          placement={tooltip.placement || 'top'}
          disableFocusListener={tooltip.disabled}
          disableHoverListener={tooltip.disabled}
          disableTouchListener={tooltip.disabled}
          arrow
        >
          <TextField
            {...params}
            variant="outlined"
            autoFocus={autoFocus}
            required={multiple ? false : required}
            fullWidth={fullWidth}
            id={name}
            label={label}
            name={name}
            value={input || ''}
            error={error}
            helperText={helperText}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={ref}
            InputProps={{
              ...params.InputProps,
              style: inputStyle || null,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="default" size={20} />
                  ) : (
                    requiredFor && (
                      <RequiredFieldAdornment
                        type={value ? 'valid' : requiredFor}
                      />
                    )
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        </Tooltip>
      )}
    />
  );
}

CustomAutocomplete.propTypes = {
  endpoint: PropTypes.func,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionKey: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.node,
  onChange: PropTypes.func,
  onCreated: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.number]),
  size: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  autoLoad: PropTypes.bool,
  allowNull: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  requiredFor: PropTypes.oneOf(['quote', 'study']),
  multiple: PropTypes.bool,
  cache: PropTypes.bool,
  creatable: PropTypes.bool,
  tooltip: PropTypes.shape(),
  inputStyle: PropTypes.shape(),
  renderOption: PropTypes.func,
  fullWidth: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  ref: PropTypes.any,
  additionalParams: PropTypes.shape(),
  autoFocus: PropTypes.bool,
  optionsFilter: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape()),
  popperZIndex: PropTypes.number,
};

CustomAutocomplete.defaultProps = {
  endpoint: null,
  getOptionKey: null,
  isOptionEqualToValue: null,
  name: null,
  label: null,
  onChange: null,
  onCreated: null,
  onFocus: null,
  onBlur: null,
  value: null,
  size: 'medium',
  disabled: false,
  required: false,
  autoLoad: false,
  allowNull: false,
  error: false,
  helperText: null,
  requiredFor: null,
  multiple: false,
  cache: false,
  creatable: false,
  tooltip: {
    content: '',
    placement: '',
    disabled: true,
  },
  inputStyle: null,
  renderOption: null,
  fullWidth: true,
  ref: null,
  additionalParams: {},
  autoFocus: false,
  optionsFilter: null,
  options: null,
  popperZIndex: null,
};

export default CustomAutocomplete;
